import { useState } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../utils/firebase'
import MobileBackButton from '../components/MobileBackButton'
import '../styles/Contact.css'

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState('')
  const [lastSubmitTime, setLastSubmitTime] = useState(0)
  const [submitCount, setSubmitCount] = useState(0)

  // Spam detection functions
  const containsSpam = (text) => {
    const spamKeywords = [
      'viagra', 'casino', 'lottery', 'winner', 'congratulations',
      'click here', 'free money', 'urgent', 'act now', 'limited time',
      'make money', 'work from home', 'bitcoin', 'crypto', 'investment'
    ]
    const lowerText = text.toLowerCase()
    return spamKeywords.some(keyword => lowerText.includes(keyword))
  }

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const disposableEmailDomains = [
      '10minutemail', 'tempmail', 'guerrillamail', 'mailinator',
      'throwaway', 'temp-mail', 'yopmail', 'sharklasers'
    ]
    return emailRegex.test(email) && 
           !disposableEmailDomains.some(domain => email.toLowerCase().includes(domain))
  }

  const checkRateLimit = () => {
    const now = Date.now()
    const timeSinceLastSubmit = now - lastSubmitTime
    
    // Reset counter if more than 30 minutes has passed
    if (timeSinceLastSubmit > 1800000) {
      setSubmitCount(0)
      return true
    }
    
    // Allow max 3 contact submissions per 30 minutes
    return submitCount < 3
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('')

    // Spam protection checks
    if (!checkRateLimit()) {
      setSubmitStatus('rateLimit')
      setIsSubmitting(false)
      return
    }

    if (!isValidEmail(formData.email)) {
      setSubmitStatus('invalidEmail')
      setIsSubmitting(false)
      return
    }

    if (containsSpam(formData.message) || containsSpam(formData.subject) || containsSpam(formData.name)) {
      setSubmitStatus('spam')
      setIsSubmitting(false)
      return
    }

    // Check for minimum content requirements
    if (formData.message.length < 20) {
      setSubmitStatus('tooShort')
      setIsSubmitting(false)
      return
    }

    try {
      // Add email to Firestore mail collection for Trigger Email extension
      await addDoc(collection(db, 'mail'), {
        to: ['breakout.dating@gmail.com'], // Replace with your actual email
        message: {
          subject: `Breakout: ${formData.subject}`,
          html: `
            <h3>Breakout - New Contact Form Submission</h3>
            <p><strong>Name:</strong> ${formData.name}</p>
            <p><strong>Email:</strong> ${formData.email}</p>
            <p><strong>Subject:</strong> ${formData.subject}</p>
            <p><strong>Message:</strong></p>
            <p>${formData.message.replace(/\n/g, '<br>')}</p>
            <hr>
            <p><small>Submitted: ${new Date().toLocaleString()}</small></p>
          `,
          text: `
New Contact Form Submission

Name: ${formData.name}
Email: ${formData.email}
Subject: ${formData.subject}

Message:
${formData.message}

---
Submitted: ${new Date().toLocaleString()}
Reply to: ${formData.email}
          `
        },
        replyTo: formData.email,
        timestamp: serverTimestamp()
      })

      setSubmitStatus('success')
      
      // Update rate limiting
      setLastSubmitTime(Date.now())
      setSubmitCount(prev => prev + 1)
      
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      })
    } catch (error) {
      console.error('Error sending message:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <HelmetProvider>
      <div className="contact-page">
        <Helmet>
          <title>Contact Us - Breakout</title>
          <meta name="description" content="Get in touch with the Breakout team. We're here to help with questions, feedback, and support." />
          <meta name="keywords" content="contact, support, help, feedback, Breakout app" />
        </Helmet>

        <MobileBackButton to="/" label="Back to Home" />

        <div className="contact-container">
          <div className="contact-header">
            <h1>Contact Us</h1>
            <p>We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
          </div>

          <div className="contact-content">
            <div className="contact-form-section">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="What's this about?"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="Tell us how we can help..."
                  />
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="status-message success">
                    Message sent successfully! We'll get back to you soon.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="status-message error">
                    There was an error sending your message. Please try again.
                  </div>
                )}

                {submitStatus === 'rateLimit' && (
                  <div className="status-message error">
                    Too many messages sent. Please wait 30 minutes before sending another message.
                  </div>
                )}

                {submitStatus === 'invalidEmail' && (
                  <div className="status-message error">
                    Please provide a valid email address from a permanent email provider.
                  </div>
                )}

                {submitStatus === 'spam' && (
                  <div className="status-message error">
                    Your message contains prohibited content. Please revise and try again.
                  </div>
                )}

                {submitStatus === 'tooShort' && (
                  <div className="status-message error">
                    Please provide a more detailed message (minimum 20 characters).
                  </div>
                )}
              </form>
            </div>

            <div className="contact-info">
              <h3>Other Ways to Reach Us</h3>
              <div className="contact-method">
                <h4>📧 Email</h4>
                <p>hello@breakout-app.com</p>
              </div>
              
              <div className="contact-method">
                <h4>💬 Social Media</h4>
                <p>Follow us on our social channels for updates and quick responses to general questions.</p>
              </div>
              
              <div className="contact-method">
                <h4>⚡ Response Time</h4>
                <p>We typically respond within 24 hours on business days.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HelmetProvider>
  )
}

export default Contact